import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  ZoomIn, 
  ZoomOut, 
  ChevronLeft, 
  ChevronRight, 
  Target, 
  AlertTriangle,
  Clock,
  Users,
  CheckCircle
} from 'lucide-react';
import type { EnhancedPlanStep } from '../../../../../arcplugs/ops/CascadePlanningBee/index.js';

interface GanttChartProps {
  steps: EnhancedPlanStep[];
  dependencies?: DependencyEdge[];
  criticalPath?: string[];
  selectedStepId?: string;
  onStepSelect?: (stepId: string) => void;
  onStepUpdate?: (stepId: string, updates: Partial<EnhancedPlanStep>) => void;
  readonly?: boolean;
  showBaseline?: boolean;
  baselineSteps?: EnhancedPlanStep[];
}

interface DependencyEdge {
  from: string;
  to: string;
  type: 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish';
}

interface TimelineScale {
  unit: 'day' | 'week' | 'month';
  pixelsPerUnit: number;
  label: string;
}

interface TaskBar {
  step: EnhancedPlanStep;
  x: number;
  width: number;
  y: number;
  height: number;
  isCritical: boolean;
  isSelected: boolean;
}

const TIMELINE_SCALES: TimelineScale[] = [
  { unit: 'day', pixelsPerUnit: 30, label: 'Days' },
  { unit: 'week', pixelsPerUnit: 100, label: 'Weeks' },
  { unit: 'month', pixelsPerUnit: 200, label: 'Months' }
];

const TASK_HEIGHT = 40; // Increased height for card-style tasks
const TASK_MARGIN = 10; // Increased margin for better spacing
const ROW_HEIGHT = TASK_HEIGHT + TASK_MARGIN * 2;
const HEADER_HEIGHT = 80;

export const EnhancedGanttChart: React.FC<GanttChartProps> = ({
  steps,
  dependencies = [],
  criticalPath = [],
  selectedStepId,
  onStepSelect,
  onStepUpdate,
  readonly = false,
  showBaseline = false,
  baselineSteps = []
}) => {
  const [scaleIndex, setScaleIndex] = useState(1);
  const [viewportStart, setViewportStart] = useState(new Date());
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    stepId: string;
    startX: number;
    originalStart: Date;
  } | null>(null);

  const currentScale = TIMELINE_SCALES[scaleIndex];

  const projectStart = useMemo(() => {
    if (steps.length === 0) return new Date();
    
    const dates = steps.map(step => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + (step.dependencies.length * 2));
      return startDate;
    });
    
    return new Date(Math.min(...dates.map(d => d.getTime())));
  }, [steps]);

  const projectEnd = useMemo(() => {
    if (steps.length === 0) return new Date();
    
    const dates = steps.map(step => {
      const startDate = new Date(projectStart);
      startDate.setDate(startDate.getDate() + (step.dependencies.length * 2));
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + step.estimatedDuration);
      return endDate;
    });
    
    return new Date(Math.max(...dates.map(d => d.getTime())));
  }, [steps, projectStart]);

  const hierarchicalSteps = useMemo(() => {
    const stepMap = new Map<string, EnhancedPlanStep>();
    steps.forEach(step => stepMap.set(step.id, step));

    const rootSteps = steps.filter(step => !step.parentId);
    const result: EnhancedPlanStep[] = [];

    const addStepAndChildren = (step: EnhancedPlanStep, level: number = 0) => {
      result.push({ ...step, level });
      
      const children = steps
        .filter(s => s.parentId === step.id)
        .sort((a, b) => a.wbsNumber.localeCompare(b.wbsNumber, undefined, { numeric: true }));
      
      children.forEach(child => addStepAndChildren(child, level + 1));
    };

    rootSteps
      .sort((a, b) => a.wbsNumber.localeCompare(b.wbsNumber, undefined, { numeric: true }))
      .forEach(step => addStepAndChildren(step));

    return result;
  }, [steps]);

  const taskBars = useMemo((): TaskBar[] => {
    return hierarchicalSteps.map((step, index) => {
      const startDate = new Date(projectStart);
      startDate.setDate(startDate.getDate() + (step.dependencies.length * 2));
      
      const daysDiff = Math.floor((startDate.getTime() - viewportStart.getTime()) / (1000 * 60 * 60 * 24));
      
      let x: number;
      let width: number;
      
      if (currentScale.unit === 'day') {
        x = daysDiff * currentScale.pixelsPerUnit;
        width = step.estimatedDuration * currentScale.pixelsPerUnit;
      } else if (currentScale.unit === 'week') {
        x = (daysDiff / 7) * currentScale.pixelsPerUnit;
        width = (step.estimatedDuration / 7) * currentScale.pixelsPerUnit;
      } else {
        x = (daysDiff / 30) * currentScale.pixelsPerUnit;
        width = (step.estimatedDuration / 30) * currentScale.pixelsPerUnit;
      }
      
      return {
        step,
        x: Math.max(0, x),
        width: Math.max(100, width), // Minimum width for card-style tasks
        y: index * ROW_HEIGHT + HEADER_HEIGHT,
        height: TASK_HEIGHT,
        isCritical: criticalPath.includes(step.id),
        isSelected: selectedStepId === step.id
      };
    });
  }, [hierarchicalSteps, projectStart, viewportStart, currentScale, criticalPath, selectedStepId]);

  const dependencyLines = useMemo(() => {
    const lines: Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      isCritical: boolean;
    }> = [];

    dependencies.forEach(dep => {
      const fromBar = taskBars.find(bar => bar.step.id === dep.from);
      const toBar = taskBars.find(bar => bar.step.id === dep.to);
      
      if (fromBar && toBar) {
        const isCritical = criticalPath.includes(dep.from) && criticalPath.includes(dep.to);
        
        lines.push({
          x1: fromBar.x + fromBar.width,
          y1: fromBar.y + fromBar.height / 2,
          x2: toBar.x,
          y2: toBar.y + toBar.height / 2,
          isCritical
        });
      }
    });

    return lines;
  }, [taskBars, dependencies, criticalPath]);

  const timelineHeaders = useMemo(() => {
    const headers: Array<{ label: string; x: number; width: number }> = [];
    const totalDays = Math.ceil((projectEnd.getTime() - projectStart.getTime()) / (1000 * 60 * 60 * 24));
    
    const currentDate = new Date(viewportStart);
    let x = 0;
    
    if (currentScale.unit === 'day') {
      for (let i = 0; i < totalDays; i++) {
        const label = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        headers.push({ label, x, width: currentScale.pixelsPerUnit });
        x += currentScale.pixelsPerUnit;
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (currentScale.unit === 'week') {
      const totalWeeks = Math.ceil(totalDays / 7);
      for (let i = 0; i < totalWeeks; i++) {
        const label = `Week ${i + 1}`;
        headers.push({ label, x, width: currentScale.pixelsPerUnit });
        x += currentScale.pixelsPerUnit;
      }
    } else {
      const totalMonths = Math.ceil(totalDays / 30);
      for (let i = 0; i < totalMonths; i++) {
        const label = currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        headers.push({ label, x, width: currentScale.pixelsPerUnit });
        x += currentScale.pixelsPerUnit;
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }
    
    return headers;
  }, [viewportStart, projectStart, projectEnd, currentScale]);

  const handleZoomIn = useCallback(() => {
    if (scaleIndex > 0) {
      setScaleIndex(scaleIndex - 1);
    }
  }, [scaleIndex]);

  const handleZoomOut = useCallback(() => {
    if (scaleIndex < TIMELINE_SCALES.length - 1) {
      setScaleIndex(scaleIndex + 1);
    }
  }, [scaleIndex]);

  const handlePanLeft = useCallback(() => {
    const newStart = new Date(viewportStart);
    newStart.setDate(newStart.getDate() - 7);
    setViewportStart(newStart);
  }, [viewportStart]);

  const handlePanRight = useCallback(() => {
    const newStart = new Date(viewportStart);
    newStart.setDate(newStart.getDate() + 7);
    setViewportStart(newStart);
  }, [viewportStart]);

  const handleTaskClick = useCallback((stepId: string) => {
    if (onStepSelect) {
      onStepSelect(stepId);
    }
  }, [onStepSelect]);

  const handleTaskDragStart = useCallback((e: React.MouseEvent, stepId: string) => {
    if (readonly) return;
    
    const step = steps.find(s => s.id === stepId);
    if (!step) return;
    
    const startDate = new Date(projectStart);
    startDate.setDate(startDate.getDate() + (step.dependencies.length * 2));
    
    setDragState({
      isDragging: true,
      stepId,
      startX: e.clientX,
      originalStart: startDate
    });
  }, [readonly, steps, projectStart]);

  const handleMouseMove = useCallback((_e: React.MouseEvent) => {
    if (!dragState || !dragState.isDragging) return;
  }, [dragState]);

  const handleMouseUp = useCallback(() => {
    if (dragState && onStepUpdate) {
      setDragState(null);
    }
  }, [dragState, onStepUpdate]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return { bg: '#ef4444', border: '#ef4444', text: 'text-white' };
      case 'high': return { bg: '#4267A1', border: '#4267A1', text: 'text-white' };
      case 'medium': return { bg: '#8A9150', border: '#8A9150', text: 'text-white' };
      case 'low': return { bg: '#10b981', border: '#10b981', text: 'text-white' };
      default: return { bg: '#6b7280', border: '#6b7280', text: 'text-white' };
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'analysis': return '🔍';
      case 'implementation': return '⚙️';
      case 'validation': return '✅';
      case 'deployment': return '🚀';
      default: return '📋';
    }
  };

  const todayX = useMemo(() => {
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - viewportStart.getTime()) / (1000 * 60 * 60 * 24));
    
    if (currentScale.unit === 'day') {
      return daysDiff * currentScale.pixelsPerUnit;
    } else if (currentScale.unit === 'week') {
      return (daysDiff / 7) * currentScale.pixelsPerUnit;
    } else {
      return (daysDiff / 30) * currentScale.pixelsPerUnit;
    }
  }, [viewportStart, currentScale]);

  if (steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-12 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-lg">
        <div className="mb-4 flex items-center justify-center rounded-full w-16 h-16 bg-gradient-to-br from-[#4267A1]/15 to-[#5A7BC4]/10 text-[#4267A1] shadow-xl shadow-[#4267A1]/20">
          <Calendar className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">No Tasks to Display</h3>
        <p className="text-gray-600">Add tasks to see them in the Gantt chart view.</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-white to-gray-50 flex">
      {/* Left Sidebar - Tyler Style */}
      <div className="w-80 bg-gradient-to-br from-gray-50 to-white border-r border-gray-200 flex flex-col shadow-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-gradient-to-br from-[#4267A1]/15 to-[#5A7BC4]/10 shadow-lg shadow-[#4267A1]/20">
              <Calendar className="w-5 h-5 text-[#4267A1]" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Project Tasks</h2>
          </div>
          
          {/* Filters */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-700 font-medium min-w-[50px]">Show:</span>
              <select className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-gray-800 text-sm focus:border-[#4267A1]/50 focus:ring-1 focus:ring-[#4267A1]/20 transition-all shadow-sm">
                <option>All Tasks</option>
                <option>Active Tasks</option>
                <option>Completed Tasks</option>
              </select>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-700 font-medium min-w-[50px]">Sort by:</span>
              <select className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-gray-800 text-sm focus:border-[#4267A1]/50 focus:ring-1 focus:ring-[#4267A1]/20 transition-all shadow-sm">
                <option>Due Date</option>
                <option>Priority</option>
                <option>Progress</option>
              </select>
            </div>
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-3">
            {hierarchicalSteps.map((step, index) => {
              const priorityColors = getPriorityColor(step.priority);
              return (
                <motion.div
                  key={step.id}
                  className="p-5 rounded-xl bg-gradient-to-br from-white to-gray-50 border border-gray-200 hover:border-[#4267A1]/40 hover:shadow-xl hover:shadow-[#4267A1]/10 transition-all cursor-pointer shadow-lg backdrop-blur-sm"
                  whileHover={{ scale: 1.02, y: -2 }}
                  onClick={() => handleTaskClick(step.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-bold text-gray-800 text-sm leading-tight tracking-tight">{step.name}</h3>
                    <div className="flex items-center gap-1.5">
                      {step.resources?.slice(0, 2).map((member, idx) => (
                        <div key={idx} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4267A1] to-[#5A7BC4] flex items-center justify-center text-xs text-white font-bold border-2 border-white shadow-md ring-1 ring-[#4267A1]/20">
                          {member.charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {step.resources && step.resources.length > 2 && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8A9150] to-[#9BA55F] flex items-center justify-center text-xs text-white font-semibold border-2 border-white ring-1 ring-[#8A9150]/20">
                          +{step.resources.length - 2}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-700 mb-4 font-semibold">
                    <span className="bg-gray-100 px-2.5 py-1 rounded-lg mr-2 border border-gray-200">{step.estimatedDuration}d</span>
                    <span className="text-gray-800 font-bold capitalize">{step.priority}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                    <div 
                      className="h-3 rounded-full transition-all duration-500 shadow-sm"
                      style={{ 
                        width: `${Math.floor(Math.random() * 100)}%`,
                        background: `linear-gradient(90deg, ${priorityColors.bg}, ${priorityColors.bg}ee)`
                      }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Timeline Area - Tyler Style */}
      <div className="flex-1 flex flex-col bg-gradient-to-br from-white to-gray-50">
        {/* Timeline Header */}
        <div className="bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-bold text-gray-800 tracking-tight">Timeline View</h3>
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleZoomOut}
                  disabled={scaleIndex >= TIMELINE_SCALES.length - 1}
                  className="p-2.5 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4267A1] hover:to-[#5A7BC4] text-gray-700 hover:text-white disabled:opacity-50 border border-gray-300 hover:border-[#4267A1]/50 transition-all shadow-sm"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button 
                  onClick={handleZoomIn}
                  disabled={scaleIndex <= 0}
                  className="p-2.5 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4267A1] hover:to-[#5A7BC4] text-gray-700 hover:text-white disabled:opacity-50 border border-gray-300 hover:border-[#4267A1]/50 transition-all shadow-sm"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-sm text-gray-700 font-medium">
                Show: <span className="font-bold text-[#4267A1]">2 Week</span>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={handlePanLeft}
                  className="p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#8A9150] hover:to-[#9BA55F] text-gray-700 hover:text-white transition-all shadow-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-800 px-4 py-2 bg-white rounded-xl font-medium border border-gray-300 shadow-sm">Today</span>
                <button 
                  onClick={handlePanRight}
                  className="p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#8A9150] hover:to-[#9BA55F] text-gray-700 hover:text-white transition-all shadow-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Week Headers - Tyler Style */}
          <div className="grid grid-cols-4 gap-4">
            {timelineHeaders.slice(0, 4).map((header, index) => (
              <div
                key={index}
                className="text-center py-4 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 hover:border-[#4267A1]/30 hover:shadow-lg hover:shadow-[#4267A1]/10 transition-all shadow-sm"
              >
                <div className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Week</div>
                <div className="font-bold text-gray-800 text-sm">Week {index + 1}</div>
                <div className="text-xs text-gray-600 mt-2 font-medium">
                  {['Sun', 'Mon', 'Wed', 'Fri'][index]}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Project Timeline Bars - Tyler Style */}
        <div className="flex-1 overflow-auto bg-gradient-to-br from-white to-gray-50 p-6">
          <div className="space-y-4">
            {/* Today Line */}
            {todayX >= 0 && (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#4267A1] to-[#5A7BC4] z-20 shadow-lg shadow-[#4267A1]/30"
                style={{ left: `${todayX}px` }}
              />
            )}

            {/* Project Bars */}
            {taskBars.map((bar, index) => {
              const priorityColors = getPriorityColor(bar.step.priority);
              const mockProgress = Math.floor(Math.random() * 100);
              
              return (
                <motion.div
                  key={bar.step.id}
                  className="relative"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Project Bar */}
                  <div
                    className="h-16 rounded-xl shadow-lg border overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-[#4267A1]/20 transition-all relative backdrop-blur-sm"
                    style={{
                      background: `linear-gradient(135deg, ${priorityColors.bg}15, ${priorityColors.bg}25)`,
                      borderColor: `${priorityColors.border}40`
                    }}
                    onClick={() => handleTaskClick(bar.step.id)}
                  >
                    <div className="flex items-center justify-between h-full px-5">
                      <div className="flex items-center gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-800 text-sm leading-tight">{bar.step.name}</h4>
                          <p className="text-xs text-gray-600 font-medium mt-0.5 capitalize">{bar.step.type}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Team Avatars */}
                        <div className="flex items-center gap-1.5">
                          {bar.step.resources?.slice(0, 2).map((member, idx) => (
                            <div key={idx} className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4267A1] to-[#5A7BC4] flex items-center justify-center text-xs text-white font-semibold border border-white shadow-sm">
                              {member.charAt(0).toUpperCase()}
                            </div>
                          ))}
                          {bar.step.resources && bar.step.resources.length > 2 && (
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#8A9150] to-[#9BA55F] flex items-center justify-center text-xs text-white border border-white">
                              +{bar.step.resources.length - 2}
                            </div>
                          )}
                        </div>
                        
                        {/* Progress */}
                        <div className="text-sm text-white font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] px-3 py-1.5 rounded-lg shadow-lg shadow-[#4267A1]/30">
                          {mockProgress}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-200">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${mockProgress}%` }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        className="h-full bg-gradient-to-r from-[#8A9150] to-[#9BA55F] shadow-sm"
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedGanttChart;
